import React, {
    ChangeEvent,
    FC,
    ReactElement,
    RefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import { notifyAboutException, notifyAboutSuccess } from 'src/utils/notify';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { downloadMaterialSegment } from 'src/downloadFile';
import { DropdownChangeEvent } from 'primereact/dropdown';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { confirmTooltip } from 'src/utils/confirm';
import { LegacyModal } from 'src/components/Modal/LegacyModal';
import { InputText } from 'primereact/inputtext';
import { Helmet } from 'react-helmet-async';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { MaterialEditingPopupContent } from 'src/pages/Materials/components/MaterialEditingPopup/Content/MaterialEditingPopupContent';
import { SubjectShort } from 'src/@types/modules/subjects/subjects.types';
import { useGetSubjectsQuery } from 'src/api/endpoints/subjects/api';
import { Dropdown as AppDropDown } from 'src/components/Dropdown';
import { skipToken } from '@reduxjs/toolkit/query';
import { SelectItem } from 'primereact/selectitem';
import { useNumberParam } from 'src/hooks/params';
import { SingleParamFunction } from 'src/@types';
import { Nullable } from 'primereact/ts-helpers';
import { ResponseError } from 'src/api';
import { filter, map } from 'lodash';
import {
    formatMaterialName,
    isMaterialLegacy,
    Material,
    useCreateMaterialMutation,
    useDeleteMaterialMutation,
    useGetMaterialsQuery,
} from 'src/api/endpoints/materials';
import {
    NavigateFunction,
    useNavigate,
    useSearchParams,
} from 'react-router-dom';
import Loader from 'src/components/Loader';
import './module.scss';
import classNames from 'classnames';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { UserRole } from 'src/api/endpoints/users/users.types.new';

export interface Form {
    subjectId: number;
    index: number;
    name: string;
    materialLink?: string;
    materialFiles: File[];
    homeworkFiles: File[];
    practiceFiles: File[];
    presentationFiles: File[];
}

interface MaterialsPageProps {
    allowEdit?: boolean;
}

export const MaterialsPage: FC<MaterialsPageProps & AuthorizedUserProps> = (
    props: MaterialsPageProps & AuthorizedUserProps
): ReactElement => {
    const [searchParams] = useSearchParams();
    const navigate: NavigateFunction = useNavigate();

    const { allowEdit = false } = props;

    const toast: RefObject<Toast> = useRef<Toast>(null);

    const [createMaterial] = useCreateMaterialMutation();
    const [deleteMaterial] = useDeleteMaterialMutation();

    const [modal, setModal] = useState<{
        id: number | null;
        visible: boolean;
    }>({ id: null, visible: false });

    // region Subjects
    const [selectedSubject, selectSubject] = useState<SubjectShort | null>(
        null
    );
    const [selectedSubjectId, selectSubjectId] = useNumberParam({
        fieldName: 'subjectId',
        ref: selectedSubject,
        searchParams,
    });

    const { data: subjects, isLoading: isSubjectsLoading } =
        useGetSubjectsQuery();

    useEffect((): void => {
        if (!subjects || isSubjectsLoading) return;

        const foundedSubject: Nullable<SubjectShort> = subjects.find(
            (subject: SubjectShort): boolean => subject.id === selectedSubjectId
        );

        if (!foundedSubject) return selectSubjectId(null);

        selectSubject(foundedSubject);
    }, [isSubjectsLoading, selectedSubjectId, subjects]);
    // endregion

    // region Materials
    const [materials, setMaterials] = useState<Material[]>([]);
    const [loadingMaterialId, setLoadingMaterialId] = useState<number | null>(
        null
    );

    const { data: materialResponse, isLoading: isMaterialsLoading } =
        useGetMaterialsQuery(
            selectedSubjectId !== null
                ? { query: { subjectId: selectedSubjectId } }
                : skipToken
        );

    useEffect((): void => {
        if (isMaterialsLoading) return;
        if (!materialResponse) return;

        setMaterials(materialResponse);
    }, [isMaterialsLoading, materialResponse]);
    // endregion

    const onHide: VoidFunction = (): void => {
        setModal({ id: null, visible: false });
    };

    const onMaterialCreate: SingleParamFunction<Form> = (form: Form): void => {
        const { subjectId, ...body } = form;

        createMaterial({ params: { subjectId }, body })
            .unwrap()
            .then((): void => {
                notifyAboutSuccess({
                    toast: toast,
                    content: 'Материал был успешно создан',
                });
                selectSubjectId(subjectId);
                onHide();
            })
            .catch((reason: ResponseError): void => {
                notifyAboutException({
                    toast: toast,
                    content: reason.message,
                });
            });
    };

    // region Materials Search
    const [filteredMaterials, setFilteredMaterials] =
        useState<Material[]>(materials);
    const [search, setSearch] = useState<string>('');

    useEffect((): void => {
        if (!searchParams.has('search')) return;
        setSearch(searchParams.get('search') ?? '');
    }, [searchParams]);

    useEffect((): void => {
        if (!search.trim().length) {
            searchParams.delete('search');
        } else {
            searchParams.set('search', search.trim());
        }

        navigate({ search: searchParams.toString() });
    }, [searchParams, search]);

    useEffect((): void => {
        if (!materials.length) return setFilteredMaterials([]);

        if (!search.trim().length) return setFilteredMaterials(materials);

        setFilteredMaterials(
            filter(materials, (m: Material): boolean =>
                formatMaterialName(m).includes(search)
            )
        );
    }, [materials, search]);
    // endregion

    const onMaterialDeleteConfirmation = (id: number): void => {
        setLoadingMaterialId(id);

        deleteMaterial({
            params: { materialId: id },
        })
            .unwrap()
            .then((): void =>
                notifyAboutSuccess({
                    toast,
                    content: 'Материал удален',
                    sound: false,
                })
            )
            .catch((reason: ResponseError): void => {
                notifyAboutException({
                    toast,
                    content: reason.message,
                });
            })
            .finally((): void => {
                setLoadingMaterialId(null);
            });
    };

    function onUpdate(materialId: number): void {
        setModal({ id: materialId, visible: true });
    }

    const onSubjectSelect = (event: DropdownChangeEvent): void => {
        selectSubjectId(event.value ?? null);
        setSearch('');
    };

    const onMaterialSearch = (event: ChangeEvent<HTMLInputElement>): void => {
        setSearch(event.target.value);
    };

    const allow =
        allowEdit &&
        (props.permissions.canBeEditMaterials ||
        props.role === UserRole.OrganizationDirector);

    return (
        <StudentLayout pageName="materials-page" auth={props}>
            <Helmet title={'Методические материалы'} />
            <ConfirmPopup />
            <Toast ref={toast} />
            {allow ? (
                <LegacyModal
                    header={`${modal.id ? 'Редактирование' : 'Создание'} материала`}
                    visible={modal.visible}
                    onHide={onHide}
                >
                    <MaterialEditingPopupContent
                        id={modal.id}
                        subjectId={selectedSubjectId}
                        onSubmit={onMaterialCreate}
                        onValid={onHide}
                    />
                </LegacyModal>
            ) : null}
            <div className="card mt-24">
                <div className="header flex align-items-center justify-content-between materials-bg">
                    <div className="title big">Методические материалы</div>
                </div>

                <div className="content">
                    <div className="content-header">
                        <div className="flex filter-container filter-container">
                            <AppDropDown
                                className={'w-300'}
                                filter={true}
                                options={map(
                                    subjects,
                                    (subject: SubjectShort): SelectItem => ({
                                        label: subject.name,
                                        value: subject.id,
                                    })
                                )}
                                loading={isSubjectsLoading}
                                value={selectedSubjectId}
                                onChange={onSubjectSelect}
                                placeholder={'Выберите предмет'}
                            />

                            {selectedSubject && (
                                <span className="p-input-icon-right ml-24 mt">
                                    <i className="pi pi-search" />
                                    <InputText
                                        value={search}
                                        onChange={onMaterialSearch}
                                        placeholder="Поиск"
                                    />
                                </span>
                            )}
                        </div>
                        <div className="buttons">
                            {allow ? (
                                <Button
                                    onClick={() =>
                                        setModal({ id: null, visible: true })
                                    }
                                >
                                    Загрузить материалы
                                </Button>
                            ) : null}
                        </div>
                    </div>
                    <div className="grid mt-30">
                        {isMaterialsLoading ? (
                            <Loader />
                        ) : selectedSubject !== null &&
                          filteredMaterials.length ? (
                            filteredMaterials.map((material: Material) => (
                                <MaterialCard
                                    key={material.id}
                                    material={material}
                                    editable={allow}
                                    isLoading={
                                        material.id === loadingMaterialId
                                    }
                                    onUpdate={onUpdate}
                                    onDelete={onMaterialDeleteConfirmation}
                                />
                            ))
                        ) : (
                            <div className="fz-20 px-2">
                                {selectedSubject ? 'Здесь пока ничего нет' : ''}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </StudentLayout>
    );
};

export type MaterialCardProps = {
    material: Material;
    onUpdate: (id: number) => Promise<void> | void;
    onDelete: (id: number) => Promise<void> | void;
    editable?: boolean;
    isLoading: boolean;
};

export const defaultMaterialCardProps = {
    editable: false,
    isLoading: false,
};

export const MaterialCard: FC<MaterialCardProps> = (
    specified: MaterialCardProps
): ReactElement => {
    const props: MaterialCardProps & typeof defaultMaterialCardProps = {
        ...defaultMaterialCardProps,
        ...specified,
    };
    const { material, editable, isLoading, onUpdate, onDelete } = props;

    const _ = useDateFormatter();

    return (
        <div
            className={classNames(
                'lg:col-3 md:col-6 col-12',
                'material-card-wrapper',
                { legacy: editable && isMaterialLegacy(material) }
            )}
        >
            <div className="area material bg flex flex-column justify-content-between h-full">
                <div
                    className={'body flex flex-column justify-content-between'}
                >
                    <div className={'flex flex-column'}>
                        <div className="flex justify-content-between preview-container">
                            <div
                                className="source-label"
                                style={{
                                    color: '#3B82F6',
                                    backgroundColor: '#3B82F620',
                                }}
                            >
                                {'У'}
                            </div>
                            <small className="date block ml-72">
                                {_(material.createdAt)}
                            </small>
                        </div>
                        <div className="text bold mt-24 mb-64">
                            {formatMaterialName(material)}
                        </div>
                    </div>
                    <div className={'flex flex-wrap'} style={{ gap: 12 }}>
                        {editable && (
                            <Button
                                style={{ flex: 1 }}
                                className="edit-button p-button-sm"
                                onClick={_ => onUpdate(material.id)}
                                loading={isLoading}
                                icon={'pi pi-pencil'}
                            />
                        )}

                        {editable && (
                            <Button
                                style={{ flex: 1 }}
                                className="p-button-sm"
                                loading={isLoading}
                                onClick={event =>
                                    confirmTooltip(event.currentTarget, {
                                        message:
                                            'Вы уверены что хотите удалить?',
                                        accept: (): void => {
                                            onDelete(material.id);
                                        },
                                    })
                                }
                                icon={'pi pi-trash'}
                            />
                        )}

                        {material.sourceLink && (
                            <Button
                                className={'p-button-sm flex-grow-1'}
                                loading={isLoading}
                                icon={'pi pi-external-link'}
                                onClick={() => {
                                    if (!material.sourceLink) return;

                                    window
                                        .open(material.sourceLink, '_blank')
                                        ?.focus();
                                }}
                                label={'Внешняя ссылка'}
                            />
                        )}

                        {material.meta.hasLesson && (
                            <DownloadMaterialFilesButton
                                material={material}
                                segment={'lesson'}
                                label={'Занятие'}
                            />
                        )}

                        {material.meta.hasHomework && (
                            <DownloadMaterialFilesButton
                                material={material}
                                segment={'homework'}
                                label={'Домашнее задание'}
                            />
                        )}

                        {material.meta.hasPresentation && (
                            <DownloadMaterialFilesButton
                                material={material}
                                segment={'presentation'}
                                label={'Презентация'}
                            />
                        )}

                        {material.meta.hasPractice && (
                            <DownloadMaterialFilesButton
                                material={material}
                                segment={'practice'}
                                label={'Практическая'}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const DownloadMaterialFilesButton = (props: {
    material: Material;
    segment: 'lesson' | 'homework' | 'practice' | 'presentation';
    label: string;
}) => {
    const { material, segment, label } = props;

    const [isLoading, setLoading] = useState(false);

    const onDownload = (): void => {
        setLoading(true);

        downloadMaterialSegment(material, segment)
            .catch((error: ResponseError) => {
                notifyAboutException({
                    content: error.message,
                    sound: true,
                });
            })
            .finally((): void => {
                setLoading(false);
            });
    };

    return (
        <Button
            className="p-button-sm flex-grow-1"
            loading={isLoading}
            icon={'pi pi-download'}
            onClick={onDownload}
            label={label}
        />
    );
};
